import type { PlayerService } from '../player/playerSystem';
import type { Vehicle } from '../vehicles/types';
import type { VehicleService } from '../vehicles/vehicleSystem';
import { createTyreParams, ENGINE_DOPPLER_C, engineDistanceCutoff, EngineDriver, firingFrequency, GENERIC_PROFILE, profileFor, tyreParamsInto, type EngineProfile, type TyreParams } from './engineModel';
import { EngineVoiceGraph, type Spatial } from './engineVoice';
import type { Mixer } from './mixer';
import { dopplerRatio } from './siren';
import { distanceGain, panFor, radialVelocityAway, type Listener } from './spatial';

/**
 * Engine + tyre + scrape voice pool. The player's vehicle always owns voice 0 (full quality); the
 * nearest awake vehicles (max 4, within 60 m) get the other voices, with distance, air absorption,
 * pan and a gentle Doppler. The drive model (`EngineDriver`) runs even when the AudioContext is
 * suspended (so hooks/tests can inspect it); nodes are only built and driven once the context runs.
 */

const POOL_SIZE = 5;
const MAX_OTHERS = POOL_SIZE - 1;
export const ENGINE_RANGE = 60;
const RANGE_SQ = ENGINE_RANGE * ENGINE_RANGE;
const SCAN_INTERVAL = 0.15;
const RELEASE_TIME = 0.45;
/** Other cars' overrun pops are only worth it close by. */
const POP_RANGE = 25;

type VoiceState = 'free' | 'active' | 'releasing';

class EngineVoice {
  vehicle: Vehicle | null = null;
  state: VoiceState = 'free';
  isPlayer = false;
  profile: EngineProfile = GENERIC_PROFILE;
  readonly driver = new EngineDriver();
  readonly tyre: TyreParams = createTyreParams();
  readonly spatial: Spatial = { gain: 0, pan: 0, cutoff: 16000, pitch: 1 };
  /** Distance gain (1 for the player's own car). */
  level = 0;
  releaseLeft = 0;
  fresh = false;
  graph: EngineVoiceGraph | null = null;
  connected = false;
}

export class EnginePool {
  private readonly voices: EngineVoice[] = [];
  private scan = 0;
  private readonly candV: (Vehicle | null)[] = new Array<Vehicle | null>(MAX_OTHERS).fill(null);
  private readonly candD: number[] = new Array<number>(MAX_OTHERS).fill(Infinity);
  private readonly wanted: (Vehicle | null)[] = new Array<Vehicle | null>(POOL_SIZE).fill(null);
  /** 0..1 road wetness (rain): less squeal, more hiss. */
  wet = 0;
  /** Other cars voiced (<= 4; `otherEngines` by render tier: 2 on LOW). */
  maxOthers = MAX_OTHERS;

  constructor(
    private readonly mx: Mixer,
    private readonly listener: Listener,
  ) {
    for (let i = 0; i < POOL_SIZE; i++) this.voices.push(new EngineVoice());
  }

  get activeCount(): number {
    let n = 0;
    for (const v of this.voices) if (v.state !== 'free') n++;
    return n;
  }

  /** Plain-data view for the `audioState` hook (deterministic: no wander, so snapshots stay reproducible). */
  describe(): { vehicleId: number; spec: string; player: boolean; gain: number; freq: number; rpm: number; on: number }[] {
    const out: { vehicleId: number; spec: string; player: boolean; gain: number; freq: number; rpm: number; on: number }[] = [];
    for (const v of this.voices) {
      if (v.state !== 'active' || !v.vehicle) continue;
      const p = v.driver.params;
      out.push({ vehicleId: v.vehicle.id, spec: v.vehicle.spec.id, player: v.isPlayer, gain: round(p.gain * v.level), freq: round(firingFrequency(v.driver.drive.rpm, v.profile.cylinders)), rpm: Math.round(v.driver.drive.rpm), on: round(p.on) });
    }
    return out;
  }

  update(dt: number, vehicles: VehicleService | undefined, player: PlayerService | undefined): void {
    if (!vehicles) return;
    this.scan -= dt;
    if (this.scan <= 0) {
      this.scan = SCAN_INTERVAL;
      this.reassign(vehicles, player);
    }
    const mx = this.mx;
    const live = mx.running && mx.ready;
    const now = mx.now();
    for (const v of this.voices) {
      if (v.state === 'free') continue;
      if (v.state === 'releasing') {
        v.releaseLeft -= dt;
        if (live && v.graph) v.graph.fadeOut(now);
        if (v.releaseLeft <= 0) this.free(v);
        continue;
      }
      const veh = v.vehicle;
      if (!veh || vehicles.get(veh.id) !== veh || veh.destroyed) {
        this.release(v);
        continue;
      }
      try {
        this.drive(v, veh, dt, live, now);
      } catch (e) {
        // One bad vehicle must never take the frame loop down.
        this.release(v);
        if (typeof console !== 'undefined') console.warn('[audio] engine voice failed', e);
      }
    }
  }

  private drive(v: EngineVoice, veh: Vehicle, dt: number, live: boolean, now: number): void {
    const tel = veh.telemetry;
    const l = this.listener;
    const p = v.driver.step(
      v.profile,
      { rpm: tel.rpm, throttle: tel.throttle, reversing: tel.reversing, forwardSpeed: tel.forwardSpeed, maxSpeed: veh.spec.maxSpeed, gears: veh.spec.gears, wheelspin: tel.wheelspin },
      dt,
    );
    const dx = veh.pos.x - l.x;
    const dz = veh.pos.z - l.z;
    const d = Math.sqrt(dx * dx + dz * dz);
    v.level = v.isPlayer ? 1 : distanceGain(d);
    const sp = v.spatial;
    sp.gain = v.level;
    if (v.isPlayer) {
      sp.pan = 0;
      sp.cutoff = 16000;
      sp.pitch = 1;
    } else {
      sp.pan = panFor(dx, dz, l.rx, l.rz);
      sp.cutoff = engineDistanceCutoff(d);
      sp.pitch = dopplerRatio(radialVelocityAway(dx, dz, veh.velocity.x, veh.velocity.z, l.vx, l.vz), ENGINE_DOPPLER_C);
    }
    tyreParamsInto(v.tyre, tel.skid, veh.speed, tel.scrape, this.wet);
    const pop = v.isPlayer || d < POP_RANGE ? v.driver.pop(v.profile, veh.speed, dt) : 0;
    if (!live) return;
    let g = v.graph;
    if (!g) {
      g = v.graph = new EngineVoiceGraph({ ctx: this.mx.ctx as AudioContext, noise: this.mx.noise }, v.profile);
    }
    if (!v.connected) {
      // The player's own car on its own bus: it is never muffled by the environment (mixer.ts).
      g.out.connect(v.isPlayer ? this.mx.buses.ownEngine : this.mx.buses.engine);
      v.connected = true;
    }
    if (v.fresh) g.setProfile(v.profile);
    g.apply(p, v.tyre, sp, now, v.fresh, v.isPlayer ? 1 : 0.6);
    v.fresh = false;
    if (pop > 0) g.pop(now + 0.005, pop * (v.isPlayer ? 1 : 0.7));
  }

  private reassign(vehicles: VehicleService, player: PlayerService | undefined): void {
    const l = this.listener;
    const playerVeh = player && player.mode === 'driving' ? player.vehicle : null;
    const cv = this.candV;
    const cd = this.candD;
    const lim = Math.max(1, Math.min(MAX_OTHERS, this.maxOthers));
    for (let i = 0; i < MAX_OTHERS; i++) {
      cv[i] = null;
      cd[i] = Infinity;
    }
    for (const veh of vehicles.all()) {
      if (veh === playerVeh || veh.destroyed) continue;
      if (!(veh.speed > 0.5 || veh.controller !== 'none' || veh.occupant !== null)) continue;
      const dx = veh.pos.x - l.x;
      const dz = veh.pos.z - l.z;
      const d2 = dx * dx + dz * dz;
      if (d2 > RANGE_SQ || d2 >= (cd[lim - 1] as number)) continue;
      // Insertion into the sorted top-N.
      let i = lim - 1;
      while (i > 0 && d2 < (cd[i - 1] as number)) {
        cd[i] = cd[i - 1] as number;
        cv[i] = cv[i - 1] as Vehicle | null;
        i--;
      }
      cd[i] = d2;
      cv[i] = veh;
    }
    const wanted = this.wanted;
    let n = 0;
    if (playerVeh && !playerVeh.destroyed) wanted[n++] = playerVeh;
    for (let i = 0; i < lim; i++) if (cv[i]) wanted[n++] = cv[i] as Vehicle;
    for (let i = n; i < POOL_SIZE; i++) wanted[i] = null;

    // Release voices whose vehicle fell out of the wanted set (or whose player-ness changed).
    for (const v of this.voices) {
      if (v.state !== 'active') continue;
      let keep = false;
      for (let i = 0; i < n; i++) if (wanted[i] === v.vehicle) keep = true;
      if (keep && v.isPlayer !== (v.vehicle === playerVeh)) keep = false;
      if (!keep) this.release(v);
    }
    // Assign free voices to wanted vehicles that have none.
    for (let i = 0; i < n; i++) {
      const veh = wanted[i] as Vehicle;
      let has = false;
      for (const v of this.voices) if (v.state === 'active' && v.vehicle === veh) has = true;
      if (has) continue;
      const free = this.voices.find((v) => v.state === 'free');
      if (!free) break;
      this.assign(free, veh, veh === playerVeh);
    }
  }

  private assign(v: EngineVoice, veh: Vehicle, isPlayer: boolean): void {
    v.vehicle = veh;
    v.state = 'active';
    v.isPlayer = isPlayer;
    v.profile = profileFor(veh.spec.id);
    v.driver.reset();
    v.level = 0;
    v.fresh = true;
  }

  private release(v: EngineVoice): void {
    v.state = 'releasing';
    v.releaseLeft = RELEASE_TIME;
  }

  private free(v: EngineVoice): void {
    if (v.graph && v.connected) {
      v.graph.out.disconnect();
      v.connected = false;
    }
    v.state = 'free';
    v.vehicle = null;
    v.level = 0;
  }

  dispose(): void {
    for (const v of this.voices) {
      v.graph?.dispose();
      v.graph = null;
      v.connected = false;
    }
  }
}

function round(x: number): number {
  return Math.round(x * 1000) / 1000;
}
